import type { RenewalPlanView } from '@zola/svc-marketplace-ts-types';

import type { ReduxCreditBalanceView } from '~/actions/vendors/types/vendorCreditActionTypes';
import { formatUnlimitedCreditExpiration } from '~/pages/vendors/Inquiries/components/CreditPrice/CreditPrice';
import type { RenewalPlanState } from '~/reducers/vendors/creditRenewalReducer';

import { formatCreditCount } from './formatters';

type PartialRenewalPlan = Partial<Pick<RenewalPlanView, 'recentPackage' | 'upcomingPackage'>>;

/**
 * Determines whether the renewal plan is a free trial
 */
export const isFreeTrial = (renewalPlan?: PartialRenewalPlan | null) => {
  return Boolean(renewalPlan?.recentPackage?.freeTrial);
};

/**
 * Determines whether the renewal plan is a free trial without a purchased plan
 */
export const isFreeTrialWithoutPurchasedPlan = (renewalPlan?: PartialRenewalPlan | null) => {
  return Boolean(renewalPlan?.recentPackage?.freeTrial) && !Boolean(renewalPlan?.upcomingPackage);
};

type PartialCreditBalanceView = Pick<
  ReduxCreditBalanceView,
  'creditBalance' | 'unlimitedCreditsActive' | 'discountActiveUntil'
>;

type PartialRenewalPlanState = Partial<
  Pick<RenewalPlanState, 'recentPackage' | 'upcomingPackage' | 'subscription' | 'loaded'>
>;

/**
 * Gets a text description of a storefront's credit balance or renewal plan
 */
export const getCreditText = (
  isPublished: boolean,
  balance: PartialCreditBalanceView,
  renewalPlan: PartialRenewalPlanState
) => {
  const { creditBalance, unlimitedCreditsActive, discountActiveUntil } = balance;
  const { recentPackage, upcomingPackage, subscription, loaded } = renewalPlan;

  if (!isPublished || loaded === false) {
    return null;
  }

  if (!unlimitedCreditsActive) {
    return formatCreditCount(creditBalance);
  }

  if (isFreeTrial(renewalPlan)) {
    if (!upcomingPackage) {
      return recentPackage?.name || 'Free trial'; // The package name should include "Free trial" copy
    }
    const starts = formatUnlimitedCreditExpiration(upcomingPackage.activeFrom);
    return `Purchased plan starts ${starts}`;
  }

  if (subscription?.nextChargeAt) {
    const renews = formatUnlimitedCreditExpiration(subscription.nextChargeAt);
    return `Renews on ${renews}`;
  }

  const expires = formatUnlimitedCreditExpiration(discountActiveUntil);
  return `Expires on ${expires}`;
};
